import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Key, Plus, Copy, Eye, EyeOff, RefreshCw, Trash2, Edit2, 
  Search, ExternalLink, Shield, CheckCircle2, AlertCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Password strength colors
const strengthColors = {
  weak: { bg: "bg-red-100", text: "text-red-700", bar: "bg-red-500" },
  medium: { bg: "bg-yellow-100", text: "text-yellow-700", bar: "bg-yellow-500" },
  strong: { bg: "bg-green-100", text: "text-green-700", bar: "bg-green-500" },
  very_strong: { bg: "bg-emerald-100", text: "text-emerald-700", bar: "bg-emerald-500" }
};

const strengthLabels = {
  weak: "Weak",
  medium: "Medium",
  strong: "Strong",
  very_strong: "Very Strong"
};

const strengthWidth = {
  weak: "25%",
  medium: "50%",
  strong: "75%",
  very_strong: "100%"
};

export default function PasswordManager() {
  const [passwords, setPasswords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    password: "",
    website_url: "",
    notes: ""
  });
  
  // Generator state - defaults optimized for website compatibility
  const [generatorOptions, setGeneratorOptions] = useState({
    length: 16,
    include_uppercase: true,
    include_lowercase: true,
    include_numbers: true,
    include_symbols: true,
    exclude_ambiguous: true,  // Exclude confusing characters like 0/O, 1/l
    website_safe: true        // Use only widely-accepted symbols
  });
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [showGeneratorInDialog, setShowGeneratorInDialog] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    };
  };

  useEffect(() => {
    loadPasswords();
  }, []);

  const loadPasswords = async () => {
    try {
      const response = await fetch(`${API_URL}/api/passwords`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPasswords(data);
      }
    } catch (error) {
      toast.error("Failed to load passwords");
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = async () => {
    try {
      const response = await fetch(`${API_URL}/api/passwords/generate`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(generatorOptions)
      });
      if (response.ok) {
        const data = await response.json();
        setGeneratedPassword(data);
        return data.password;
      } else {
        const error = await response.text();
        console.error("Generate password error:", error);
        toast.error("Failed to generate password");
      }
    } catch (error) {
      console.error("Generate password exception:", error);
      toast.error("Failed to generate password");
    }
    return null;
  };

  const handleGenerateAndUse = async () => {
    const password = await generatePassword();
    if (password) {
      setFormData(prev => ({ ...prev, password }));
      setShowGeneratorInDialog(false);
      toast.success("Password generated and applied");
    } else {
      toast.error("Could not generate password");
    }
  };

  const copyToClipboard = async (text, label = "Password") => {
    // Trim any accidental whitespace
    const cleanText = text.trim();
    
    if (!cleanText) {
      toast.error("Nothing to copy");
      return;
    }

    // Method 1: Modern Clipboard API with permission check
    try {
      if (navigator.clipboard && window.isSecureContext) {
        // Try to get permission first
        const permissionStatus = await navigator.permissions?.query?.({ name: 'clipboard-write' }).catch(() => null);
        
        if (!permissionStatus || permissionStatus.state === 'granted' || permissionStatus.state === 'prompt') {
          await navigator.clipboard.writeText(cleanText);
          toast.success(`${label} copied! (${cleanText.length} characters)`);
          return;
        }
      }
    } catch (err) {
      // Fall through to fallback
    }

    // Method 2: Fallback using execCommand
    try {
      const textArea = document.createElement("textarea");
      textArea.value = cleanText;
      // Make it invisible but still part of the document
      textArea.style.cssText = "position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;boxShadow:none;background:transparent;";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, cleanText.length); // For mobile
      
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success(`${label} copied! (${cleanText.length} characters)`);
        return;
      }
    } catch (err) {
      // Fall through to manual
    }

    // Method 3: Manual copy dialog
    toast.info("Please copy manually from the dialog");
    window.prompt("Copy this password (Ctrl+C or Cmd+C):", cleanText);
  };

  const togglePasswordVisibility = (id) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openAddDialog = () => {
    setEditingEntry(null);
    setFormData({ name: "", username: "", password: "", website_url: "", notes: "" });
    setGeneratedPassword(null);
    setShowGeneratorInDialog(false);
    setShowDialog(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      name: entry.name,
      username: entry.username || "",
      password: entry.password,
      website_url: entry.website_url || "",
      notes: entry.notes || ""
    });
    setGeneratedPassword(null);
    setShowGeneratorInDialog(false);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.password) {
      toast.error("Name and Password are required");
      return;
    }

    try {
      const url = editingEntry 
        ? `${API_URL}/api/passwords/${editingEntry.id}`
        : `${API_URL}/api/passwords`;
      
      const response = await fetch(url, {
        method: editingEntry ? "PUT" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingEntry ? "Password updated" : "Password saved");
        setShowDialog(false);
        loadPasswords();
      } else {
        toast.error("Failed to save password");
      }
    } catch (error) {
      toast.error("Failed to save password");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this password?")) return;

    try {
      const response = await fetch(`${API_URL}/api/passwords/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success("Password deleted");
        loadPasswords();
      } else {
        toast.error("Failed to delete password");
      }
    } catch (error) {
      toast.error("Failed to delete password");
    }
  };

  const filteredPasswords = passwords.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.username && p.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.website_url && p.website_url.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  return (
    <div className="space-y-6" data-testid="password-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Key className="h-6 w-6 text-primary" />
            Password Manager
          </h1>
          <p className="text-muted-foreground mt-1">
            Securely store and manage your work passwords
          </p>
        </div>
        <Button onClick={openAddDialog} data-testid="add-password-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Password
        </Button>
      </div>

      {/* Quick Generator Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-foreground">
            <RefreshCw className="h-5 w-5 text-primary" />
            Quick Password Generator
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm text-foreground mb-1 block">Length: {generatorOptions.length}</Label>
              <Slider
                value={[generatorOptions.length]}
                onValueChange={([v]) => setGeneratorOptions(prev => ({ ...prev, length: v }))}
                min={8}
                max={64}
                step={1}
                className="w-full"
              />
            </div>
            <div className="flex gap-4 flex-wrap text-foreground">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={generatorOptions.include_uppercase}
                  onCheckedChange={(v) => setGeneratorOptions(prev => ({ ...prev, include_uppercase: v }))}
                />
                ABC
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={generatorOptions.include_lowercase}
                  onCheckedChange={(v) => setGeneratorOptions(prev => ({ ...prev, include_lowercase: v }))}
                />
                abc
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={generatorOptions.include_numbers}
                  onCheckedChange={(v) => setGeneratorOptions(prev => ({ ...prev, include_numbers: v }))}
                />
                123
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={generatorOptions.include_symbols}
                  onCheckedChange={(v) => setGeneratorOptions(prev => ({ ...prev, include_symbols: v }))}
                />
                @#$
              </label>
            </div>
            <Button onClick={generatePassword} variant="outline" className="border-primary/30 hover:bg-primary/10">
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate
            </Button>
          </div>
          
          {generatedPassword && (
            <div className="mt-4 p-3 bg-card rounded-lg border border-primary/20">
              <div className="flex items-center justify-between gap-4">
                <code className="font-mono text-lg flex-1 break-all text-foreground">{generatedPassword.password}</code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(generatedPassword.password)}
                  data-testid="copy-generated-btn"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${strengthColors[generatedPassword.strength]?.bar || "bg-muted-foreground"}`}
                    style={{ width: strengthWidth[generatedPassword.strength] }}
                  />
                </div>
                <span className={`text-sm font-medium ${strengthColors[generatedPassword.strength]?.text || "text-muted-foreground"}`}>
                  {strengthLabels[generatedPassword.strength]}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search passwords..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-passwords"
        />
      </div>

      {/* Passwords List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : filteredPasswords.length === 0 ? (
            <div className="p-8 text-center">
              <Shield className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">
                {searchTerm ? "No passwords match your search" : "No passwords saved yet"}
              </p>
              {!searchTerm && (
                <Button onClick={openAddDialog} variant="outline" className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Password
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Program / Website</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Password</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPasswords.map((entry) => (
                  <TableRow key={entry.id} data-testid={`password-row-${entry.id}`}>
                    <TableCell>
                      <div className="font-medium text-foreground">{entry.name}</div>
                      {entry.website_url && (
                        <a 
                          href={entry.website_url.startsWith("http") ? entry.website_url : `https://${entry.website_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          {entry.website_url}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-foreground">
                      {entry.username || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm bg-muted px-2 py-1 rounded text-foreground">
                          {visiblePasswords[entry.id] ? entry.password : "••••••••••"}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => togglePasswordVisibility(entry.id)}
                          className="h-8 w-8 p-0"
                        >
                          {visiblePasswords[entry.id] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(entry.password)}
                          className="h-8 w-8 p-0"
                          data-testid={`copy-btn-${entry.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(entry.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(entry)}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-btn-${entry.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(entry.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        data-testid={`delete-btn-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? "Update Password" : "Add New Password"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Program / Website Name *</Label>
              <Input
                id="name"
                placeholder="e.g., QuickBooks, Firm360"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="input-name"
              />
            </div>
            
            <div>
              <Label htmlFor="username">Username / Email</Label>
              <Input
                id="username"
                placeholder="Your login username or email"
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                data-testid="input-username"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="password">Password *</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowGeneratorInDialog(!showGeneratorInDialog)}
                  className="h-7 text-xs text-cyan-600"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  {showGeneratorInDialog ? "Hide Generator" : "Generate"}
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  id="password"
                  type="text"
                  placeholder="Enter or generate password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="font-mono"
                  data-testid="input-password"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => copyToClipboard(formData.password)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              
              {showGeneratorInDialog && (
                <div className="mt-3 p-3 bg-muted rounded-lg border space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground">Length: {generatorOptions.length}</span>
                    <Slider
                      value={[generatorOptions.length]}
                      onValueChange={([v]) => setGeneratorOptions(prev => ({ ...prev, length: v }))}
                      min={8}
                      max={64}
                      step={1}
                      className="flex-1"
                    />
                  </div>
                  <div className="flex flex-wrap gap-3 text-foreground">
                    <label className="flex items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={generatorOptions.include_uppercase}
                        onCheckedChange={(v) => setGeneratorOptions(prev => ({ ...prev, include_uppercase: v }))}
                      />
                      ABC
                    </label>
                    <label className="flex items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={generatorOptions.include_lowercase}
                        onCheckedChange={(v) => setGeneratorOptions(prev => ({ ...prev, include_lowercase: v }))}
                      />
                      abc
                    </label>
                    <label className="flex items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={generatorOptions.include_numbers}
                        onCheckedChange={(v) => setGeneratorOptions(prev => ({ ...prev, include_numbers: v }))}
                      />
                      123
                    </label>
                    <label className="flex items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={generatorOptions.include_symbols}
                        onCheckedChange={(v) => setGeneratorOptions(prev => ({ ...prev, include_symbols: v }))}
                      />
                      @#$
                    </label>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleGenerateAndUse}
                    className="w-full"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generate & Use
                  </Button>
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="website_url">Website URL</Label>
              <Input
                id="website_url"
                placeholder="https://example.com"
                value={formData.website_url}
                onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                data-testid="input-website"
              />
            </div>
            
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="input-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-password-btn">
              {editingEntry ? "Update Password" : "Save Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
